/**
 * Block Move Modal
 *
 * Modal for selecting a target section and position when moving
 * a block within an assessment report.
 */

import { useState, useRef, useEffect } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { type SectionType, type ParsedBlock, getSectionLabel, getValidTargetSections } from './reportHtmlUtils';

interface BlockMoveModalProps {
  blockTitle: string;
  sourceSectionType: SectionType;
  sourceIndex: number;
  /** All sections with their blocks, for building the position dropdown */
  sectionBlocks: Record<SectionType, ParsedBlock[]>;
  onMove: (toSection: SectionType, toIndex: number) => void;
  onClose: () => void;
  /** Position anchor for the modal */
  anchorRect: DOMRect | null;
}

export function BlockMoveModal({
  blockTitle,
  sourceSectionType,
  sourceIndex,
  sectionBlocks,
  onMove,
  onClose,
  anchorRect,
}: BlockMoveModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalRef, onClose);

  const validTargets = getValidTargetSections(sourceSectionType);
  const canCrossSection = validTargets.length > 1;

  const [targetSection, setTargetSection] = useState<SectionType>(sourceSectionType);

  // Build position options for the target section
  const targetBlocks = sectionBlocks[targetSection] || [];
  // Filter out the current block if same section
  const isSameSection = targetSection === sourceSectionType;

  // Position options: "Bovenaan" + "Na [block title]" for each block
  const positionOptions: Array<{ label: string; value: number }> = [
    { label: 'Bovenaan', value: 0 },
  ];

  for (let i = 0; i < targetBlocks.length; i++) {
    // Skip the current block in position list when same section
    if (isSameSection && i === sourceIndex) continue;
    const block = targetBlocks[i];
    if (!block) continue;
    // After removing sourceIndex, blocks shift: if i > sourceIndex, position is i-1, else i
    // "Na block[i]" means insert after it, so toIndex = postRemovalPosition + 1
    const toIndex = isSameSection
      ? (i < sourceIndex ? i + 1 : i) // i > sourceIndex: shifted to i-1, +1 = i
      : i + 1; // Different section: simple "after position i"
    const title = block.title.length > 40 ? block.title.slice(0, 40) + '...' : block.title;
    positionOptions.push({
      label: `Na "${title}"`,
      value: toIndex,
    });
  }

  const [targetIndex, setTargetIndex] = useState<number>(0);

  // Reset position when target section changes
  useEffect(() => {
    setTargetIndex(0);
  }, [targetSection]);

  const handleMove = () => {
    onMove(targetSection, targetIndex);
  };

  // Calculate position - place near anchor button
  const style: React.CSSProperties = {};
  if (anchorRect) {
    style.position = 'fixed';
    style.top = Math.min(anchorRect.bottom + 8, window.innerHeight - 320);
    style.right = window.innerWidth - anchorRect.right;
    style.zIndex = 100;
  }

  // Check if the selected move would be a no-op
  const isNoOp = isSameSection && (
    targetIndex === sourceIndex ||
    targetIndex === sourceIndex + 1
  );

  return (
    <div
      ref={modalRef}
      className="no-print bg-white rounded-lg shadow-2xl border border-gray-200 w-72 overflow-hidden"
      style={style}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Blok verplaatsen</h3>
        <p className="text-xs text-gray-500 mt-0.5 truncate" title={blockTitle}>
          {blockTitle}
        </p>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Target section (only show if cross-section moves are possible) */}
        {canCrossSection && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Verplaats naar
            </label>
            <select
              value={targetSection}
              onChange={(e) => setTargetSection(e.target.value as SectionType)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {validTargets.map((st) => (
                <option key={st} value={st}>
                  {getSectionLabel(st)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Position */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Positie
          </label>
          <select
            value={targetIndex}
            onChange={(e) => setTargetIndex(Number(e.target.value))}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {positionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Annuleren
        </button>
        <button
          onClick={handleMove}
          disabled={isNoOp}
          className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Verplaatsen
        </button>
      </div>
    </div>
  );
}
